import React, { useEffect, useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Info, MapPin } from 'lucide-react';

import { Link, NavLink } from 'react-router-dom';
import logo from '../../assets/svg/logo.svg';
// import logoSmall from '../../assets/svg/logo.svg';
import phone from '../../assets/svg/phone.svg';
import email from '../../assets/svg/email.svg';
import dropdownArrow from '../../assets/svg/dropdown-arrow.svg';

import { getFiles } from '../../hooks/api-methods/get-upload';
import { useScreenSize } from '../../hooks/useScreenSize';

import { useNavigate, useLocation } from 'react-router-dom';
import Loader from '../Loader/Loader';
import { getContactInfo } from '../../hooks/api-methods/get-contact-info';

interface FileItem {
  name: string;
  url: string;
}

interface NavProps {
  swiper?: SwiperType | null;
}

export interface ContactInfo {
  id: string;
  vat: string;
  email: string;
  phone: string;
  adress: string;
  mapsUrl?: string;
}

const Nav: React.FC<NavProps> = ({ swiper }) => {
  const [desktopLogoUrl, setDesktopLogoUrl] = useState<string | null>(null);
  const [mobileLogoUrl, setMobileLogoUrl] = useState<string | null>(null);
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(false);

  const { isTablet } = useScreenSize();
  const navigate = useNavigate();
  const location = useLocation();
  const activeSlide = location.state?.slide ?? 0;

  const isHome = location.pathname === '/';
  const infoRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (showInfo && infoRef.current && !infoRef.current.contains(event.target as Node)) {
        setShowInfo(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showInfo]);

  useEffect(() => {
    async function fetchFiles() {
      try {
        const result = await getFiles('logo'); // Fetch from container

        // Normalize names (case-insensitive, ignore extensions)
        const normalizeName = (name: string) => name.toLowerCase().replace(/\.[^/.]+$/, '');

        const desktopFile = result.find((file: FileItem) => normalizeName(file.name) === 'logo');
        const mobileFile = result.find(
          (file: FileItem) => normalizeName(file.name) === 'logo-small',
        );

        if (desktopFile) setDesktopLogoUrl(desktopFile.url);
        if (mobileFile) setMobileLogoUrl(mobileFile.url);
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Er is een onbekende fout opgetreden.');
        }
      } finally {
        setLoading(false);
      }
    }

    fetchFiles();
  }, []);

  useEffect(() => {
    async function fetchContactInfo() {
      try {
        const data = await getContactInfo();
        setContactInfo(data);
        console.log(data);
      } catch (err) {
        console.error('Failed to load contact info', err);
      }
    }

    fetchContactInfo();
  }, []);

  const displayLogo = isTablet ? mobileLogoUrl || logo : desktopLogoUrl || logo;

  return (
    <section className="grid-padding-lr">
      <nav className="navbar">
        <div className="flex-row gap-2">
          <div className="flex-row space-between w-100">
            <div className={`${isTablet ? 'navbar-logo-small' : 'navbar-logo'}`}>
              <Link to="/">
                {loading ? (
                  <Loader />
                ) : error ? (
                  <p style={{ color: 'red' }}>{error}</p>
                ) : (
                  <img src={displayLogo} alt="Logo" />
                )}
              </Link>
            </div>
            <ul className="navbar-desktop flex-row-align justify-end w-100">
              <li className={`dropdown ${isHome ? ' bold' : ''}`}>
                <button
                  className={`dropdown-toggle`}
                  onClick={() => navigate('/', { state: { slide: 0 } })}
                >
                  Comfort begint hier
                  <span className="dropdown-arrow">
                    <img src={dropdownArrow} alt="" />
                  </span>
                </button>

                <ul className="dropdown-menu">
                  <li onClick={() => navigate('/', { state: { slide: 0 } })}>
                    <button
                      className={`button-transparent color-prim ${isHome && activeSlide === 0 ? 'bold' : ''}`}
                    >
                      Meer dan alleen koelen!
                    </button>
                  </li>
                  <li onClick={() => navigate('/', { state: { slide: 1 } })}>
                    <button
                      className={`button-transparent color-prim ${isHome && activeSlide === 1 ? 'bold' : ''}`}
                    >
                      Gecertificeerd Koeltechnieker
                    </button>
                  </li>
                  <li onClick={() => navigate('/', { state: { slide: 2 } })}>
                    <button
                      className={`button-transparent color-prim ${isHome && activeSlide === 2 ? 'bold' : ''}`}
                    >
                      Installatie & Onderhoud
                    </button>
                  </li>
                  <li onClick={() => navigate('/', { state: { slide: 3 } })}>
                    <button
                      className={`button-transparent color-prim ${isHome && activeSlide === 3 ? 'bold' : ''}`}
                    >
                      Waar wij voor staan
                    </button>
                  </li>
                  <li onClick={() => navigate('/', { state: { slide: 4 } })}>
                    <button
                      className={`button-transparent color-prim ${isHome && activeSlide === 4 ? 'bold' : ''}`}
                    >
                      Wie we zijn!
                    </button>
                  </li>
                  <li onClick={() => navigate('/', { state: { slide: 5 } })}>
                    <button
                      className={`button-transparent color-prim ${isHome && activeSlide === 5 ? 'bold' : ''}`}
                    >
                      Contacteer ons!
                    </button>
                  </li>
                </ul>
              </li>
              <li>
                <NavLink
                  to="/installatie-voorbeelden"
                  className={({ isActive }) => (isActive ? 'bold' : undefined)}
                >
                  Installatie voorbeelden
                </NavLink>
              </li>

              <li className="icon info-icon">
                <div ref={infoRef} className="info-wrapper">
                  <button
                    className="flex-row-align"
                    type="button"
                    onClick={() => setShowInfo((prev) => !prev)}
                    aria-label="Bedrijfsinformatie"
                  >
                    <Info size={20} color="#ffffff" />
                  </button>

                  {showInfo && (
                    <div className="info-tooltip flex-colomn gap-1">
                      <strong>AirSolutions</strong>

                      {contactInfo?.adress && (
                        <a
                          href={
                            contactInfo.mapsUrl ??
                            `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              contactInfo.adress,
                            )}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="info-row flex-row-align gap-1"
                        >
                          <MapPin size={14} color="#26437c" />
                          <span>{contactInfo.adress}</span>
                        </a>
                      )}

                      {contactInfo?.vat && <div>BTW: {contactInfo.vat}</div>}
                    </div>
                  )}
                </div>
              </li>

              <li className="icon">
                {contactInfo?.email && (
                  <a href={`mailto:${contactInfo.email}`}>
                    <img src={email} alt="Email" />
                  </a>
                )}
              </li>

              <li className="icon">
                {contactInfo?.phone && (
                  <a href={`tel:${contactInfo.phone}`}>
                    <img src={phone} alt="Telefoon" />
                  </a>
                )}
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </section>
  );
};

export default Nav;
